<template>
  <section>
    <v-card class="rounded-lg" outlined>
      <v-card-title>
        <v-icon left small> fal fa-user-circle </v-icon
        >{{ $t("general") }}</v-card-title
      >
      <v-card-text>
        <v-row align="center">
          <v-col xs="12" sm="12" md="4" lg="4" xl="4" class="text-center">
            <v-badge
              bordered
              bottom
              color="primary"
              class="avatar-camera-badge"
              icon="fal fa-camera"
              overlap
            >
              <v-avatar
                color="#f5f5f5"
                size="158"
                v-if="MyRole('change_logo_store')"
              >
                <v-avatar-uploader
                  :url="urlLogo"
                  :clickable="true"
                  field="file"
                  accept="image/jpeg,image/png,image/gif,image/svg+xml"
                  :rename="rename"
                  @failed="failed"
                  :request="requestLogo"
                />
              </v-avatar>
              <v-avatar size="150" v-else>
                <v-img :src="urlLogo"></v-img>
              </v-avatar> </v-badge
          ></v-col>
          <v-divider vertical />

          <v-col xs="12" sm="12" md="8" lg="8" xl="8">
            <v-card-text>
              <AppTitle title="store_name" :value="store?.name" />
              <AppTitle title="business_type" :value="store?.store_type_code" />
              <AppTitle
                title="cost_method"
                value="Weighted Average Cost (ຕົ້ນທຶນສະເລ່ຍ)"
                :button="true"
                @showDetailedCalculation="showDetailedCalculation = true"
              />
              <AppTitle title="store_phone" :value="store?.phone_no" />
              <AppTitle title="store_address" :value="store?.address" />
              <AppTitle title="store_expire" :value="store?.end_date" />
              <AppTitle
                title="product_count"
                :value="store?.product_count | ToNumber"
              />
              <AppTitle
                v-if="store.tax_include"
                title="tax"
                :value="store?.tax"
              />
              <AppTitle
                title="description"
                :value="store?.desc"
                :divider="false"
              />
            </v-card-text>
          </v-col>
        </v-row>

        <template v-if="MyRole('change_logo_store')">
          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 font-weight-bold mb-1">
            {{ $t("store_cover") }}
          </div>
          <div class="text-caption grey--text mb-3">
            {{ $t("store_cover_hint") }}
          </div>
          <v-img
            v-if="urlCover"
            :src="urlCover"
            max-height="200"
            contain
            class="rounded-lg mb-3 grey lighten-4"
          />
          <DragDropUpload
            v-model="coverFile"
            accept="image/*"
            :max-size-mb="5"
            @file-selected="uploadCover"
          />
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-title>
        <v-icon left small> fal fa-globe-asia </v-icon>
        {{ $t("change_language") }}
      </v-card-title>
      <v-card-text>
        <v-list>
          <template v-for="(locale, index) in locales">
            <v-list-item
              :key="`locale-${index}`"
              @click="changeLocale(locale.code)"
            >
              <v-list-item-avatar size="25" class="mr-2">
                <v-img :src="locale.flag" />
              </v-list-item-avatar>

              <v-list-item-title
                :class="locale.code === $i18n.locale ? 'primary--text' : ''"
                >{{ locale.name }}</v-list-item-title
              >

              <v-list-item-icon>
                <v-icon
                  v-if="locale.code === $i18n.locale"
                  color="primary"
                  small
                >
                  fas fa-check-circle
                </v-icon>
              </v-list-item-icon>
            </v-list-item>
            <v-divider
              inset
              class="my-0"
              v-if="index < locales.length - 1"
              :key="`divider-${index}`"
            ></v-divider>
          </template>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Shortcut Menu Card -->
    <v-card class="rounded-lg mt-4" outlined>
      <v-card-title>
        <v-icon left small>fal fa-bolt</v-icon>
        {{ $t("quick_access") }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            v-for="shortcut in availableShortcuts"
            :key="shortcut.id"
            xs="12"
            sm="6"
            md="4"
            lg="4"
            xl="4"
          >
            <v-card
              class="shortcut-card rounded-lg"
              outlined
              @click="navigateTo(shortcut)"
              hover
            >
              <v-card-text class="text-center pa-4">
                <v-icon size="48" :color="shortcut.color" class="mb-3">
                  {{ shortcut.icon }}
                </v-icon>
                <h3 class="text-h6 font-weight-bold mb-2">
                  {{ $t(shortcut.title) }}
                </h3>
                <p class="text-caption text--secondary">
                  {{ $t(shortcut.description) }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Detailed Calculation Dialog -->

    <app-custom-dialog
      v-model="showDetailedCalculation"
      width="750px"
      :footer="false"
      title="detailed_weighted_average_calculation"
    >
      <div slot="activator">
        <WeightedAverageInfo />
      </div>
    </app-custom-dialog>

    <!-- Customer Screen Settings Dialog -->
    <app-custom-dialog
      v-model="showCustomerScreenDialog"
      title="customer_screen_settings"
      titleIcon="fal fa-tv"
      submitText="save"
      cancelText="cancel"
      width="540px"
      @submit="saveCustomerScreenSettings"
    >
      <div slot="activator">

        <!-- Auto-open on startup -->
        <v-card
          flat
          rounded="lg"
          :color="$vuetify.theme.dark ? 'rgba(76,175,80,0.10)' : '#f0fdf4'"
          :style="{
            border: `1.5px solid ${customerScreenSettings.autoOpenOnStartup
              ? '#4caf50'
              : ($vuetify.theme.dark ? 'rgba(76,175,80,0.25)' : 'rgba(76,175,80,0.35)')}`
          }"
          class="mb-5"
        >
          <v-card-text class="pa-3">
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-avatar
                  size="42"
                  :color="customerScreenSettings.autoOpenOnStartup ? 'success' : ($vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-3')"
                >
                  <v-icon small :color="customerScreenSettings.autoOpenOnStartup ? 'white' : 'grey'">
                    fal fa-desktop
                  </v-icon>
                </v-avatar>
              </v-col>
              <v-col>
                <div class="font-weight-bold body-2">
                  {{ $t('auto_open_on_startup') || 'Auto-open on startup' }}
                </div>
                <div class="caption" :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text'">
                  {{ $t('auto_open_startup_desc') || 'Opens when a second monitor is detected on app load' }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-switch
                  v-model="customerScreenSettings.autoOpenOnStartup"
                  color="success"
                  dense
                  inset
                  hide-details
                  class="mt-0 pt-0"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Position selector -->
        <div class="text-caption font-weight-bold grey--text mb-1 text-uppercase">
          {{ $t('secondary_screen_position') || 'Secondary Screen Position' }}
        </div>
        <div class="caption grey--text mb-3">
          {{ $t('secondary_screen_position_desc') || 'Where is your iPad / 2nd screen relative to your main monitor?' }}
        </div>
        <v-btn-toggle
          v-model="customerScreenSettings.position"
          mandatory
          color="primary"
          class="cs-position-toggle mb-4"
        >
          <v-btn
            v-for="pos in positionOptions"
            :key="pos.value"
            :value="pos.value"
            text
            height="56"
          >
            <div class="d-flex flex-column align-center">
              <v-icon small class="mb-1">{{ pos.icon }}</v-icon>
              <span class="caption text-none">{{ pos.label }}</span>
            </div>
          </v-btn>
        </v-btn-toggle>

        <v-divider class="mb-4" />

        <!-- Monitor index -->
        <TextSelect
          v-model="customerScreenSettings.monitorIndex"
          label="customer_monitor"
          :items="monitorOptions"
          text="text"
          itemValue="value"
          :returnObject="false"
          :searchable="false"
          :placeholder="false"
          class="mb-1"
        />
        <div class="caption grey--text mb-4 ml-1">
          {{ $t('customer_monitor_hint') || 'Select which display shows the customer-facing screen' }}
        </div>

        <v-divider class="mb-3" />

        <!-- Checkboxes -->
        <v-checkbox
          v-model="customerScreenSettings.fullscreen"
          :label="$t('fullscreen_mode') || 'Open in fullscreen mode'"
          color="primary"
          hide-details
          dense
          class="mt-1"
        />
        <v-checkbox
          v-model="customerScreenSettings.autoDetect"
          :label="$t('auto_detect_monitor') || 'Auto-detect secondary monitor'"
          color="primary"
          hide-details
          dense
          class="mt-1"
        />
        <v-checkbox
          v-model="customerScreenSettings.showInstructions"
          :label="$t('show_help_instructions') || 'Show help instructions'"
          color="primary"
          hide-details
          dense
          class="mt-1 mb-1"
        />
      </div>
    </app-custom-dialog>
  </section>
</template>

<script>
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import VAvatarUploader from "@/components/VAvatarUploader";
import AppTitle from "@/components/settings/app_title";
import WeightedAverageInfo from "@/components/products/WeightedAverageInfo";
import TextSelect from "@/components/global/TextSelect";
import { setUser } from "@/controllers/auth";
import { myMixin } from "@/mixin/main";
import { success, error } from "@/controllers/alert";
import { uploadErrorMessage } from "@/utils/uploadError";
import { mapGetters } from "vuex";
import customerScreenMonitor from "@/utils/customerScreenMonitor";

export default {
  props: ["store"],
  components: {
    VAvatarUploader,
    AppTitle,
    WeightedAverageInfo,
    TextSelect,
    DragDropUpload,
  },
  data() {
    return {
      coverFile: null,
      shortcuts: [
        {
          id: "kitchen",
          title: "kitchen",
          description: "kitchen_description",
          icon: "fal fa-utensils",
          color: "primary",
          route: "/kitchen",
          showFor: ["restaurant"],
        },
        {
          id: "customer_screen",
          title: "customer_screen",
          description: "customer_screen_description",
          icon: "fal fa-tv",
          color: "success",
          route: "/customer-screen",
          showFor: ["all"],
        },
        {
          id: "price_checker",
          title: "price_checker",
          description: "price_checker_description",
          icon: "fal fa-file-invoice-dollar",
          color: "info",
          route: "/price-checker",
          showFor: ["pos"],
        },
        {
          id: "customer_screen_settings",
          title: "customer_screen_settings",
          description: "customer_screen_settings_description",
          icon: "fal fa-cog",
          color: "warning",
          route: null,
          showFor: ["all"],
          action: "configureCustomerScreen",
        },
      ],
      showDetailedCalculation: false,
      showHelpVideo: false,
      showCustomerScreenDialog: false,
      customerScreenSettings: {
        autoOpenOnStartup: false,
        position: "right",
        monitorIndex: 1,
        fullscreen: true,
        autoDetect: true,
        showInstructions: true,
      },
      detectedMonitors: [],
    };
  },
  mixins: [myMixin],
  computed: {
    ...mapGetters({
      locales: "auth/locales",
    }),
    urlLogo() {
      if (this.user && this.user.stores && this.user.stores.logo) {
        if (
          this.user.stores.logo.includes("http") ||
          this.user.stores.logo.includes("https")
        ) {
          return this.user.stores.logo;
        }
        const baseURL = process.env.VUE_APP_BASE_URL;
        return `${baseURL}/uploads/${this.user.stores.logo}`;
      } else {
        return "/images/pos.png";
      }
    },
    urlCover() {
      const cover = this.user?.stores?.cover;
      if (!cover) return "";
      if (cover.includes("http") || cover.includes("https")) {
        return cover;
      }
      const baseURL = process.env.VUE_APP_BASE_URL;
      return `${baseURL}/uploads/${cover}`;
    },
    isRestaurant() {
      // Check if store type is restaurant
      return (
        this.store?.store_type_code === "restaurant" ||
        this.store?.business_type === "restaurant" ||
        this.user?.stores?.store_type_code === "restaurant"
      );
    },
    isPOSStore() {
      return (
        this.store?.store_type_code === "POS" ||
        this.user?.stores?.store_type_code === "POS"
      );
    },
    availableShortcuts() {
      return this.shortcuts.filter((shortcut) => {
        // Show for all store types
        if (shortcut.showFor.includes("all")) {
          return true;
        }
        // Show only for specific store types
        if (shortcut.showFor.includes("restaurant") && this.isRestaurant) {
          return true;
        }
        if (shortcut.showFor.includes("pos") && this.isPOSStore) {
          return true;
        }
        return false;
      });
    },
    positionOptions() {
      return [
        { value: "left",  icon: "fal fa-arrow-left",  label: this.$t("left")  || "Left"  },
        { value: "right", icon: "fal fa-arrow-right", label: this.$t("right") || "Right" },
        { value: "above", icon: "fal fa-arrow-up",    label: this.$t("above") || "Above" },
        { value: "below", icon: "fal fa-arrow-down",  label: this.$t("below") || "Below" },
      ];
    },
    monitorOptions() {
      if (this.detectedMonitors.length > 0) {
        return this.detectedMonitors.map((m, i) => ({
          text: `${m.label || `Monitor ${i + 1}`}${m.isPrimary ? ` (${this.$t("primary") || "Primary"})` : ""} — ${m.width}×${m.height}`,
          value: i,
        }));
      }
      return [
        { text: this.$t("primary_monitor")   || "Primary Monitor (Main)",       value: 0 },
        { text: this.$t("secondary_monitor") || "Secondary Monitor (Customer)", value: 1 },
      ];
    },
  },
  methods: {
    async requestLogo(form, config) {
      const { data } = await this.$http.post("change_store_logo", form, config);
      if (data.status) {
        await setUser(this);
        await success(this, data.message);
      }
    },
    async uploadCover(file) {
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await this.$http.post("change_store_cover", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.status) {
          this.coverFile = null;
          await setUser(this);
          await success(this, data.message);
        }
      } catch (err) {
        await error(this, uploadErrorMessage(err, this.$t.bind(this)));
      }
    },
    rename(file) {
      const ext = file.name.split(".")[1];
      const name = `1231232.${ext}`;
      return name;
    },
    async failed(err) {
      console.error("upload failed", err);
      await error(this, uploadErrorMessage(err, this.$t.bind(this)));
    },
    calDay(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
    navigateTo(shortcut) {
      // Handle special actions
      if (shortcut && shortcut.action === "configureCustomerScreen") {
        this.configureCustomerScreen();
        return;
      }

      // Handle string route for backward compatibility
      if (typeof shortcut === "string") {
        if (shortcut === "/customer-screen") {
          this.openCustomerScreen();
        } else {
          window.open(shortcut, "_blank");
        }
        return;
      }

      // Special handling for customer screen to open on second monitor
      if (shortcut && shortcut.route === "/customer-screen") {
        this.openCustomerScreen();
      } else if (shortcut && shortcut.route) {
        window.open(shortcut.route, "_blank");
      }
    },

    // Open customer screen on second monitor with fullscreen
    async openCustomerScreen() {
      try {
        // Check if popup blockers might be active
        if (!this.checkPopupPermissions()) {
          this.showPopupPermissionWarning();
          return;
        }

        // Use the customer screen monitor utility with async method for better detection
        const result = await customerScreenMonitor.openCustomerScreen(
          "/customer-screen"
        );

        if (result.success) {
          console.log(
            "Customer screen opened successfully on:",
            result.config.screenLabel || "secondary monitor"
          );
          // Note: Fullscreen is now handled by the customer_screen.vue page itself
        } else {
          console.warn("Customer screen opening failed:", result.error);
          this.showCustomerScreenFallback();
        }
      } catch (error) {
        console.error("Failed to open customer screen:", error);
        // Handle different types of errors with specific messages
        if (error.name === "SecurityError" || error.message.includes("popup")) {
          this.showPopupPermissionWarning();
        } else {
          this.showCustomerScreenFallback();
        }
      }
    },

    // Check if popup permissions are available
    checkPopupPermissions() {
      try {
        // Test if we can open a small popup window
        const testWindow = window.open("", "_blank", "width=1,height=1");

        if (testWindow) {
          testWindow.close();
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.warn("Popup permission check failed:", error);
        return false;
      }
    },

    // Show popup permission warning with detailed instructions
    showPopupPermissionWarning() {
      const isChrome = navigator.userAgent.indexOf("Chrome") !== -1;
      const isFirefox = navigator.userAgent.indexOf("Firefox") !== -1;

      let instructions = "";

      if (isChrome) {
        instructions = `
          <div style="text-align: left;">
            <p><strong>${
              this.$t("popup_blocked_chrome") || "Chrome Instructions:"
            }</strong></p>
            <ol style="margin: 10px 0; padding-left: 20px;">
              <li>Click the <strong>🛡️ Lock</strong> icon in the address bar (top right)</li>
              <li>Find "Pop-ups and redirects" and set it to "Allow"</li>
              <li>Click the Customer Screen button again</li>
            </ol>
          </div>
        `;
      } else if (isFirefox) {
        instructions = `
          <div style="text-align: left;">
            <p><strong>${
              this.$t("popup_blocked_firefox") || "Firefox Instructions:"
            }</strong></p>
            <ol style="margin: 10px 0; padding-left: 20px;">
              <li>Click the <strong>☰</strong> menu and select "Options"</li>
              <li>Go to "Privacy & Security" → "Permissions"</li>
              <li>Find "Block pop-up windows" and uncheck it</li>
              <li>Reload the page and try again</li>
            </ol>
          </div>
        `;
      } else {
        instructions = `
          <div style="text-align: left;">
            <p><strong>${
              this.$t("popup_blocked_general") || "General Instructions:"
            }</strong></p>
            <ol style="margin: 10px 0; padding-left: 20px;">
              <li>Check your browser settings for popup blockers</li>
              <li>Allow popups for this website (${
                window.location.hostname
              })</li>
              <li>Try clicking the Customer Screen button again</li>
            </ol>
          </div>
        `;
      }

      this.$swal
        .fire({
          icon: "warning",
          title:
            this.$t("popup_permission_required") || "Popup Permission Required",
          html: `
          <div style="text-align: left;">
            <p>${
              this.$t("popup_blocked_detailed") ||
              "The browser blocked opening the customer screen. Please allow popups for this site."
            }</p>
            ${instructions}
            <hr style="margin: 15px 0;">
            <p style="font-size: 14px; color: #666;">
              <strong>${
                this.$t("alternative_method") || "Alternative:"
              }</strong>
              ${
                this.$t("manual_open_alternative") ||
                "You can also open the customer screen manually by typing /customer-screen in the address bar."
              }
            </p>
          </div>
        `,
          confirmButtonText: this.$t("try_again") || "Try Again",
          showCancelButton: true,
          cancelButtonText: this.$t("open_manually") || "Open Manually",
          width: 600,
          customClass: {
            popup: "ps-confirm-swal popup-warning-dialog",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            // Retry after user enables popups
            setTimeout(() => this.openCustomerScreen(), 1000);
          } else {
            // Manual fallback
            window.open("/customer-screen", "_blank");
          }
        });
    },

    // Detect customer monitor configuration
    detectCustomerMonitor() {
      const defaultConfig = {
        x: window.screen.width,
        y: 0,
        width: window.screen.width,
        height: window.screen.height,
        isSecondary: true,
      };

      try {
        // Check if we can detect multiple monitors
        const totalWidth = window.screen.availWidth || window.screen.width;
        const currentWidth = window.screen.width;

        // If total width is greater than current screen width, assume multiple monitors
        if (totalWidth > currentWidth) {
          // Try to get saved configuration first
          const savedConfig = this.getCustomerMonitorConfig();
          if (savedConfig) {
            return { ...defaultConfig, ...savedConfig };
          }

          // Auto-detect: assume second monitor is to the right
          return {
            x: currentWidth,
            y: 0,
            width: currentWidth,
            height: window.screen.height,
            isSecondary: true,
          };
        }

        // Single monitor detected - use current screen
        return {
          x: 0,
          y: 0,
          width: window.screen.width,
          height: window.screen.height,
          isSecondary: false,
        };
      } catch (error) {
        console.warn("Monitor detection failed, using default:", error);
        return defaultConfig;
      }
    },

    // Build window features string for window.open
    buildWindowFeatures(monitor) {
      const features = [
        `left=${monitor.x}`,
        `top=${monitor.y}`,
        `width=${monitor.width}`,
        `height=${monitor.height}`,
        "fullscreen=yes",
        "titlebar=no",
        "location=no",
        "menubar=no",
        "scrollbars=no",
        "resizable=no",
        "status=no",
        "toolbar=no",
      ];

      return features.join(",");
    },

    // Get saved customer monitor configuration
    getCustomerMonitorConfig() {
      try {
        const config = localStorage.getItem("customerMonitorConfig");
        return config ? JSON.parse(config) : null;
      } catch (error) {
        console.warn("Failed to get monitor config:", error);
        return null;
      }
    },

    // Save customer monitor configuration
    saveCustomerMonitorConfig(config) {
      try {
        localStorage.setItem("customerMonitorConfig", JSON.stringify(config));
      } catch (error) {
        console.warn("Failed to save monitor config:", error);
      }
    },

    // Fallback when automatic opening fails
    showCustomerScreenFallback() {
      // Show user-friendly message with manual instructions
      this.$swal
        .fire({
          icon: "warning",
          title:
            this.$t("customer_screen_blocked") || "Customer Screen Blocked",
          html: `
          <div style="text-align: left;">
            <p>${
              this.$t("popup_blocked_message") ||
              "The browser blocked the automatic opening of the customer screen."
            }</p>
            <p><strong>${
              this.$t("manual_steps") || "Manual Steps:"
            }</strong></p>
            <ol style="margin: 10px 0; padding-left: 20px;">
              <li>${
                this.$t("allow_popups") || "Allow popups for this site"
              }</li>
              <li>${
                this.$t("try_again") ||
                "Try clicking the Customer Screen button again"
              }</li>
              <li>${
                this.$t("or_manual_open") ||
                "Or manually open: /customer-screen"
              }</li>
            </ol>
          </div>
        `,
          confirmButtonText: this.$t("try_again") || "Try Again",
          showCancelButton: true,
          cancelButtonText: this.$t("manual_open") || "Open Manually",
          customClass: "ps-confirm-swal",
        })
        .then((result) => {
          if (result.isConfirmed) {
            // Retry opening customer screen
            setTimeout(() => this.openCustomerScreen(), 500);
          } else {
            // Open with standard method as fallback
            window.open("/customer-screen", "_blank");
          }
        });
    },

    async configureCustomerScreen() {
      this.customerScreenSettings = { ...customerScreenMonitor.getConfig() };
      this.detectedMonitors = [];
      this.showCustomerScreenDialog = true;
      // Detect real monitor names in background; TextSelect updates reactively
      try {
        this.detectedMonitors = await customerScreenMonitor.detectMonitorsAsync();
      } catch (e) {
        // Falls back to generic labels in monitorOptions computed
      }
    },

    saveCustomerScreenSettings() {
      customerScreenMonitor.saveConfig({ ...this.customerScreenSettings });
      this.showCustomerScreenDialog = false;
      this.$swal.fire({
        icon: "success",
        title: this.$t("settings_saved") || "Settings Saved",
        text: this.$t("customer_screen_settings_updated") || "Customer screen settings updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style scoped>
.shortcut-card {
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.shortcut-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.shortcut-card .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.shortcut-card h3 {
  margin-bottom: 8px;
}

.shortcut-card p {
  margin-bottom: 0;
  line-height: 1.4;
}

/* Customer screen position toggle */
.cs-position-toggle {
  display: flex !important;
  width: 100%;
  border-radius: 10px !important;
  overflow: hidden;
}

.cs-position-toggle .v-btn {
  flex: 1;
  min-width: 0 !important;
  border-radius: 0 !important;
}

.cs-position-toggle .v-btn:first-child {
  border-radius: 10px 0 0 10px !important;
}

.cs-position-toggle .v-btn:last-child {
  border-radius: 0 10px 10px 0 !important;
}

/* Weighted Average Cost Section Styles */
.cost-method-info {
  line-height: 1.6;
}

.formula-box {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #1976d2;
}

.formula-title {
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 8px;
}

.formula-text {
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
  line-height: 1.4;
}

.example-section {
  height: 100%;
}

.example-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.example-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.example-item:last-child {
  margin-bottom: 0;
}

.example-item.result {
  font-weight: 600;
  color: #1976d2;
  font-size: 1.1rem;
}

.example-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.example-value {
  font-weight: 500;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .example-section {
    margin-top: 16px;
  }

  .formula-text {
    font-size: 0.8rem;
  }
}

/* camera badge: small circle, bottom right, icon centered */
.avatar-camera-badge ::v-deep .v-badge__badge {
  left: auto !important;
  right: 6px !important;
  bottom: 6px !important;
  top: auto !important;
  transform: none;
  min-width: 26px;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.avatar-camera-badge ::v-deep .v-badge__badge .v-icon {
  font-size: 12px !important;
  line-height: 1;
  margin: 0;
  color: #fff;
}
</style>
